'use client';
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMixpanel } from '@/hooks/useMixpanel';
import {
  useInitiateDeposit,
  usePaymentTypes,
  useQuotes,
  useSupportedAssets,
  useSupportedCountries,
} from '@/services/query/wallet.queries';
import { COUNTRY_TO_FIAT_MAP, detectUserLocation } from '@/utils';
import { useAuth } from './useAuth';
import useDebounce from './useDebounce';

import { ONRAMPER_API_KEY } from '@/constants';

export type DepositStep =
  | 'idle'
  | 'fetching_supported'
  | 'fetching_payment'
  | 'fetching_quotes'
  | 'initiating'
  | 'completed'
  | 'failed';

export function useDeposit() {
  const { user } = useAuth();
  const { trackDepositStarted } = useMixpanel();

  // --- Local User Input State ---
  const [fiatCurrency, setFiatCurrency] = useState('MXN');
  const [cryptoCurrency, setCryptoCurrency] = useState('USDC_ETHEREUM');
  const [amount, setAmount] = useState<number>(500);
  const [country, setCountry] = useState('MX');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [selectedQuoteOverride, setSelectedQuote] = useState<any>(undefined);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);

  // --- Auto-detected Geolocation State ---
  const [detectedGeo, setDetectedGeo] = useState<{
    country: string;
    currency: string;
    defaultAmount: number;
  } | null>(null);
  const hasAppliedGeoRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    detectUserLocation().then((loc) => {
      if (isMounted) {
        setDetectedGeo(loc);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const debouncedAmount = useDebounce(amount, 800);
  const debouncedCountry = useDebounce(country, 500);

  const {
    data: supportedData,
    isLoading: loadingSupported,
    error: supportedError,
  } = useSupportedAssets();

  const {
    data: supportedCountries = [],
    isLoading: loadingCountries,
    error: countriesError,
  } = useSupportedCountries();

  const {
    data: paymentTypes = [],
    isLoading: loadingPayment,
    error: paymentError,
  } = usePaymentTypes(fiatCurrency, debouncedCountry, cryptoCurrency);

  const {
    data: quotes = [],
    isLoading: loadingQuotes,
    error: quotesError,
  } = useQuotes(
    fiatCurrency,
    cryptoCurrency,
    debouncedAmount,
    debouncedCountry,
    selectedPaymentMethod
  );

  const initiateMutation = useInitiateDeposit();

  const supportedFiat = useMemo(() => supportedData?.fiat || [], [supportedData]);
  const supportedCrypto = useMemo(() => supportedData?.crypto || [], [supportedData]);

  // Synchronize detected location with Onramper support lists
  useEffect(() => {
    if (
      !detectedGeo ||
      hasAppliedGeoRef.current ||
      loadingCountries ||
      loadingSupported ||
      supportedCountries.length === 0 ||
      supportedFiat.length === 0
    ) {
      return;
    }

    hasAppliedGeoRef.current = true;

    // 1. Verify country
    let finalCountry = detectedGeo.country;
    const isCountrySupported = supportedCountries.some((c: any) => c.code === detectedGeo.country);
    if (!isCountrySupported) {
      const hasMX = supportedCountries.some((c: any) => c.code === 'MX');
      const hasUS = supportedCountries.some((c: any) => c.code === 'US');
      finalCountry = hasMX ? 'MX' : hasUS ? 'US' : supportedCountries[0]?.code || 'MX';
    }

    // 2. Verify fiat currency
    let finalCurrency = detectedGeo.currency;
    const isFiatSupported = supportedFiat.some((f: any) => f.code === detectedGeo.currency);
    if (!isFiatSupported) {
      const hasMXN = supportedFiat.some((f: any) => f.code === 'MXN');
      const hasUSD = supportedFiat.some((f: any) => f.code === 'USD');
      finalCurrency = hasMXN ? 'MXN' : hasUSD ? 'USD' : supportedFiat[0]?.code || 'MXN';
    }

    // 3. Determine amount
    let finalAmount = detectedGeo.defaultAmount;
    if (finalCurrency !== detectedGeo.currency) {
      const matchingCountry = Object.keys(COUNTRY_TO_FIAT_MAP).find(
        (k) => COUNTRY_TO_FIAT_MAP[k].currency === finalCurrency
      );
      finalAmount = matchingCountry ? COUNTRY_TO_FIAT_MAP[matchingCountry].defaultAmount : 100;
    }

    // Batch all updates in startTransition — avoids synchronous setState in effect body
    startTransition(() => {
      setCountry(finalCountry);
      setFiatCurrency(finalCurrency);
      setAmount(finalAmount);
    });
  }, [
    detectedGeo,
    supportedCountries,
    supportedFiat,
    loadingCountries,
    loadingSupported,
    selectedPaymentMethod,
  ]);

  const finalPaymentTypes = useMemo(() => {
    // Use static types as a base
    const base = [...paymentTypes];

    // Extract dynamic types from quotes
    const dynamicMap = new Map<string, any>();
    quotes.forEach((q: any) => {
      if (Array.isArray(q.availablePaymentMethods)) {
        q.availablePaymentMethods.forEach((m: any) => {
          if (!dynamicMap.has(m.paymentTypeId)) {
            dynamicMap.set(m.paymentTypeId, m);
          }
        });
      }
    });

    if (dynamicMap.size === 0) return base;
    return Array.from(dynamicMap.values());
  }, [paymentTypes, quotes]);

  const isStillValid = finalPaymentTypes.some((t) => t.paymentTypeId === selectedPaymentMethod);
  if (finalPaymentTypes.length > 0 && (!selectedPaymentMethod || !isStillValid)) {
    const speiOption = finalPaymentTypes.find(
      (t: any) =>
        t.paymentTypeId?.toLowerCase().includes('spei') || t.name?.toLowerCase().includes('spei')
    );
    const cardOption = finalPaymentTypes.find(
      (t: any) =>
        t.paymentTypeId?.toLowerCase().includes('card') || t.name?.toLowerCase().includes('card')
    );
    const nextMethod = speiOption
      ? speiOption.paymentTypeId
      : cardOption
        ? cardOption.paymentTypeId
        : finalPaymentTypes[0].paymentTypeId;

    if (selectedPaymentMethod !== nextMethod) {
      setSelectedPaymentMethod(nextMethod);
    }
  }

  const selectedQuote = useMemo(() => {
    // 1. Honor manual override if it's still valid
    if (selectedQuoteOverride !== undefined && selectedQuoteOverride !== null) {
      const freshQuote = quotes.find((q: any) => q.ramp === selectedQuoteOverride.ramp);
      const isOverrideValid =
        freshQuote &&
        freshQuote.payout > 0 &&
        (!freshQuote.errors || freshQuote.errors.length === 0);
      if (isOverrideValid) return freshQuote;
    }

    // 2. Otherwise, pick the best valid quote
    const validQuotes = quotes.filter(
      (q: any) => q.payout > 0 && (!q.errors || q.errors.length === 0)
    );
    const sortedValid = [...validQuotes].sort((a, b) => (b.payout || 0) - (a.payout || 0));

    if (sortedValid.length > 0) return sortedValid[0];

    // 3. Fallback: if all quotes are invalid/errors, just sort them anyway so we don't crash
    const sortedAll = [...quotes].sort((a, b) => (b.payout || 0) - (a.payout || 0));
    return sortedAll.length > 0 ? sortedAll[0] : null;
  }, [quotes, selectedQuoteOverride]);

  // Map step based on query statuses
  const step: DepositStep = useMemo(() => {
    if (initiateMutation.isPending) return 'initiating';
    if (initiateMutation.isSuccess) return 'completed';
    if (initiateMutation.isError) return 'failed';
    if (loadingSupported) return 'fetching_supported';
    if (loadingPayment) return 'fetching_payment';
    if (loadingQuotes) return 'fetching_quotes';
    return 'idle';
  }, [
    initiateMutation.isPending,
    initiateMutation.isSuccess,
    initiateMutation.isError,
    loadingSupported,
    loadingPayment,
    loadingQuotes,
    supportedError,
    paymentError,
    quotesError,
  ]);

  // --- Auto-selection Logic (without useEffect for defaults) ---

  // 1. Determine the automatically selected quote (if user hasn't chosen one)
  const autoSelectedQuote = useMemo(() => {
    if (quotes.length > 0) return quotes[0];
    return null;
  }, [quotes]);

  const effectiveQuote = selectedQuote || autoSelectedQuote;

  // 2. Determine the automatically selected payment method
  const autoSelectedPaymentMethod = useMemo(() => {
    if (finalPaymentTypes.length === 0) return '';

    // Check if the current user-selected method is still valid
    const isStillValid = finalPaymentTypes.some((t) => t.paymentTypeId === selectedPaymentMethod);
    if (selectedPaymentMethod && isStillValid) return selectedPaymentMethod;

    const speiOption = finalPaymentTypes.find(
      (t: any) =>
        t.paymentTypeId?.toLowerCase().includes('spei') || t.name?.toLowerCase().includes('spei')
    );
    const cardOption = finalPaymentTypes.find(
      (t: any) =>
        t.paymentTypeId?.toLowerCase().includes('card') || t.name?.toLowerCase().includes('card')
    );

    return speiOption
      ? speiOption.paymentTypeId
      : cardOption
        ? cardOption.paymentTypeId
        : finalPaymentTypes[0].paymentTypeId;
  }, [finalPaymentTypes, selectedPaymentMethod]);

  const effectivePaymentMethod = selectedPaymentMethod || autoSelectedPaymentMethod;

  const initiateDeposit = async () => {
    if (!effectiveQuote) return;
    try {
      trackDepositStarted({
        deposit_method: effectivePaymentMethod,
        amount: amount,
        currency: fiatCurrency,
      });
      const result = await initiateMutation.mutateAsync({
        onramp: effectiveQuote.ramp,
        amount,
        sourceCurrency: fiatCurrency,
        destinationCurrency: cryptoCurrency,
        paymentMethod: effectivePaymentMethod,
        type: 'BUY',
        country: debouncedCountry,
      });

      // The backend returns widgetUrl = "https://buy.onramper.com?session=<id>"
      // We just need to append our apiKey to make it embeddable in an iframe.
      let finalUrl = result?.data?.widgetUrl || result?.data?.paymentUrl;

      if (finalUrl && finalUrl.includes('buy.onramper.com')) {
        try {
          const urlObj = new URL(finalUrl);
          if (!urlObj.searchParams.get('apiKey') && ONRAMPER_API_KEY) {
            urlObj.searchParams.set('apiKey', ONRAMPER_API_KEY);
          }
          finalUrl = urlObj.toString();
        } catch (e) {
          console.error('Failed to parse Onramper URL', e);
        }
      }

      if (finalUrl) {
        setPaymentUrl(finalUrl);
      }
    } catch (err) {
      console.error('Failed to initiate deposit', err);
    }
  };

  const reset = useCallback(() => {
    setCryptoCurrency('USDC_ETHEREUM');
    setSelectedPaymentMethod('');
    setSelectedQuote(undefined);
    setPaymentUrl(null);
    initiateMutation.reset();

    hasAppliedGeoRef.current = false;
    if (detectedGeo) {
      let finalCountry = detectedGeo.country;
      const isCountrySupported = supportedCountries.some(
        (c: any) => c.code === detectedGeo.country
      );
      if (!isCountrySupported) {
        const hasMX = supportedCountries.some((c: any) => c.code === 'MX');
        const hasUS = supportedCountries.some((c: any) => c.code === 'US');
        finalCountry = hasMX ? 'MX' : hasUS ? 'US' : supportedCountries[0]?.code || 'MX';
      }

      let finalCurrency = detectedGeo.currency;
      const isFiatSupported = supportedFiat.some((f: any) => f.code === detectedGeo.currency);
      if (!isFiatSupported) {
        const hasMXN = supportedFiat.some((f: any) => f.code === 'MXN');
        const hasUSD = supportedFiat.some((f: any) => f.code === 'USD');
        finalCurrency = hasMXN ? 'MXN' : hasUSD ? 'USD' : supportedFiat[0]?.code || 'MXN';
      }

      let finalAmount = detectedGeo.defaultAmount;
      if (finalCurrency !== detectedGeo.currency) {
        const matchingCountry = Object.keys(COUNTRY_TO_FIAT_MAP).find(
          (k) => COUNTRY_TO_FIAT_MAP[k].currency === finalCurrency
        );
        finalAmount = matchingCountry ? COUNTRY_TO_FIAT_MAP[matchingCountry].defaultAmount : 100;
      }

      setCountry(finalCountry);
      setFiatCurrency(finalCurrency);
      setAmount(finalAmount);
    } else {
      setCountry('MX');
      setFiatCurrency('MXN');
      setAmount(500);
    }
  }, [initiateMutation, detectedGeo, supportedCountries, supportedFiat]);

  const error =
    supportedError?.message ||
    paymentError?.message ||
    quotesError?.message ||
    initiateMutation.error?.message ||
    null;

  return {
    step,
    fiatCurrency,
    setFiatCurrency,
    cryptoCurrency,
    setCryptoCurrency,
    amount,
    setAmount,
    country,
    setCountry,
    selectedPaymentMethod: effectivePaymentMethod,
    setSelectedPaymentMethod,
    supportedFiat,
    supportedCrypto,
    supportedCountries,
    paymentTypes: finalPaymentTypes,
    quotes,
    error,
    selectedQuote: effectiveQuote,
    setSelectedQuote,
    initiateDeposit,
    paymentUrl,
    setPaymentUrl,
    reset,
    isLoading: loadingSupported || loadingPayment || loadingQuotes || loadingCountries,
    isLoadingQuotes: loadingQuotes,
    isInitiating: initiateMutation.isPending,
    isTyping:
      amount !== undefined && debouncedAmount !== undefined
        ? amount.toString() !== debouncedAmount.toString()
        : false,
  };
}
